import React, {Fragment} from 'react';

import classnames from 'classnames/bind';

import {getHeadingColourFromTextColour} from '@edume/bento/slides';
import {getProviderByUrl} from '@edume/bento/thirdPartyProviders';
import {isValidEmbeddedUrl} from '@edume/bento/validation';
import {getVideoUrl} from '@edume/bento/videoUtil.browser';
import {CompletionScreen, Lesson} from '@edume/magnificent';

import {EDITING_TYPES, QUESTION_TYPES} from '../../../enums/lessons';
import {lookupMagnificentFontColour} from '../../../utils/colours';
import {getButtonParam} from '../../../utils/progressButton';
import QuestionPreview from '../ActivityAuthoring/Preview/QuestionPreview';
import VideoPreview from '../ActivityAuthoring/Preview/VideoPreview';
import AssessmentPreviewButton from './AssessmentPreviewButton';
import {WrappedImageSlide} from './WrappedImageSlide';

import styles from '../../Lesson/AuthoringLayout/Overview/ScreenOverview/styles.module.scss';

const cx = classnames.bind(styles);

const {TextSlide, LearningOutcomesSlide, ResultsSlide, PreviewNavButton} =
  Lesson;

const getTextColourOrFallback = (fallback) => (hex) =>
  lookupMagnificentFontColour(hex, fallback);
const getTextColourOrWhite = getTextColourOrFallback('white');
const getTextColourOrTextPrimary = getTextColourOrFallback('textPrimary');

export const getSlideName = (slide) => {
  if (slide.type === 'text' && slide.params.image !== null) {
    return slide.params.image.position === 'bottom'
      ? 'imageBottom'
      : 'imageTop';
  } else {
    return slide.type;
  }
};

const getIntlCompletionTicksAriaLabel =
  (intl) =>
  ({completedActivitiesCount, totalActivitiesCount}) =>
    intl.formatMessage(
      {id: 'CompletionTicks.label'},
      {completedActivitiesCount, totalActivitiesCount}
    );

export const getSlideElements =
  (editingType, isAssessment) =>
  // eslint-disable-next-line complexity
  (slide, slideIndex) => {
    /**
     * ephemeralClientSideSlideId is generated client-side and is NOT globally unique.
     * It should for client-side operations only and not to signify uniqueness across lessons.
     */
    const ephemeralClientSideSlideId = slide.id;
    switch (slide.type) {
      case 'interactive': {
        const elements = [
          {
            name: 'taskBox',
            slideIndex,
            ephemeralClientSideSlideId,
            imagePathOverride: 'slideshowTaskBox',
            params: {
              taskBoxText: slide.params.taskBoxText,
              taskBoxTitle: slide.params.taskBoxTitle,
              background: slide.params.background,
              fontColour: slide.params.fontColour,
            },
            type: slide.params.interaction.type,
          },
        ];
        const {interaction} = slide.params;
        const hasVideoElement =
          interaction.params.quizType &&
          !isAssessment &&
          (interaction.type === 'standaloneQuiz' ||
            interaction.type === 'ordering');
        if (hasVideoElement) {
          elements.push({
            name: 'video',
            slideIndex,
            ephemeralClientSideSlideId,
            params: slide.params,
          });
        }
        if (interaction.params.questions) {
          interaction.params.questions.forEach((_q, questionIndex) => {
            elements.push({
              name: 'question',
              questionIndex,
              slideIndex,
              ephemeralClientSideSlideId,
              params: {
                background: slide.params.background,
                fontColour: slide.params.fontColour,
                ...interaction.params,
              },
            });
          });
          if (editingType === EDITING_TYPES.FULL) {
            elements.push({
              name: 'addQuestion',
              slideIndex,
              ephemeralClientSideSlideId,
            });
          }
        }
        if (interaction.type === 'ordering') {
          elements.push({
            name: 'orderingTask',
            slideIndex,
            ephemeralClientSideSlideId,
            params: {
              background: slide.params.background,
              fontColour: slide.params.fontColour,
              ...interaction.params,
            },
          });
        }
        return elements;
      }
      case 'thirdParty': {
        const {hideIntroSlide} = slide.params;
        const thirdPartySlides = [];
        if (!hideIntroSlide) {
          thirdPartySlides.push('thirdPartyIntro');
        }
        thirdPartySlides.push('thirdParty');

        return thirdPartySlides.map((name) => ({
          name,
          slideIndex,
          ephemeralClientSideSlideId,
          params: slide.params,
        }));
      }
      default:
        return {
          name: getSlideName(slide),
          slideIndex,
          ephemeralClientSideSlideId,
        };
    }
  };

const getMessage = (key, values = {}, intl) =>
  intl.formatMessage({id: `LessonCanvas.${key}`}, values);

// eslint-disable-next-line complexity
const getTextComponent = ({type: name, intl, params}) => {
  const {
    title,
    description,
    background,
    image,
    fontColour,
    button,
    isAssessment,
    isPreviewMode = false,
    isLightButtonStyle,
    hidePreviewButtons,
    isUsingHtmlEditor,
  } = params;
  const textColor = getTextColourOrTextPrimary(fontColour);
  const prefix = name === 'intro' ? 'EditIntro' : 'EditText';

  const defaultTitle = isAssessment
    ? getMessage(`${prefix}.assessmentTitlePlaceholder`, {}, intl)
    : getMessage(`${prefix}.titlePlaceholder`, {}, intl);
  const defaultDescription = isAssessment
    ? getMessage(`${prefix}.assessmentDescriptionPlaceholder`, {}, intl)
    : getMessage(`${prefix}.descriptionPlaceholder`, {}, intl);

  const buttonDefaultText = intl.formatMessage({
    id: name === 'intro' ? 'Lesson.preview.btnIntro' : 'Button.next',
  });

  return (
    <TextSlide
      title={title || defaultTitle}
      description={description || defaultDescription}
      background={background}
      transition={true}
      editMode={true}
      image={image}
      textColor={textColor}
      isButtonLightTheme={isLightButtonStyle}
      button={!hidePreviewButtons && getButtonParam(button, buttonDefaultText)}
      isPreviewMode={isPreviewMode}
      isUsingHtmlEditor={isUsingHtmlEditor}
    />
  );
};

const getImageSlideComponent = ({params, overlay, intl}) => {
  const {
    altText,
    background,
    image,
    isPreviewMode = false,
    isLightButtonStyle,
    isMultiLanguage,
    setSelectedObject,
    selectedObject,
    showDeleteTextWarning,
    setShowDeleteTextWarning,
    removeObject,
    deleteTextWarning,
  } = params;

  return (
    <WrappedImageSlide
      altText={altText}
      background={background}
      transition={true}
      editMode={true}
      image={image}
      showButtons
      isButtonLightTheme={isLightButtonStyle}
      isPreviewMode={isPreviewMode}
      disableMoveableInteractions={isPreviewMode}
      overlayElements={overlay}
      removeText={intl.formatMessage({
        id: 'Guides.creatorComponent.editor.toolbar.remove',
      })}
      dynamicTextPlaceholder={intl.formatMessage({
        id: `LessonCanvas.EditImageSlide.textPlaceholder`,
      })}
      isMultiLanguage={isMultiLanguage}
      setSelectedObject={setSelectedObject}
      selectedObject={selectedObject}
      showDeleteTextWarning={showDeleteTextWarning}
      setShowDeleteTextWarning={setShowDeleteTextWarning}
      removeObject={removeObject}
      deleteTextWarning={deleteTextWarning}
    />
  );
};

export const getEndComponent = ({intl, params}) => {
  const {
    title,
    subtitle,
    background,
    icon,
    fontColour,
    button,
    isLightButtonStyle,
    endOfActivityCustomTextOptions,
    isPreviewMode = false,
  } = params;

  const numLessons = params.numberOfModuleActivities;

  const textColor = fontColour || 'textPrimary';

  const displayTitle = endOfActivityCustomTextOptions
    ? title
    : intl.formatMessage({id: 'LessonCanvas.EditEnd.Preview.defaultTitle'});
  const displaySubtitle = endOfActivityCustomTextOptions
    ? subtitle
    : intl.formatMessage({id: 'LessonCanvas.EditEnd.Preview.defaultSubtitle'});

  const displayButtonText = endOfActivityCustomTextOptions
    ? button.text
    : intl.formatMessage({id: 'Button.next'});

  const numberOfActivitiesText = intl.formatMessage(
    {id: 'Learning.course.activityCount'},
    {count: numLessons}
  );

  return (
    <CompletionScreen
      numberOfActivitiesText={numberOfActivitiesText}
      title={displayTitle}
      subtitle={displaySubtitle}
      buttonText={displayButtonText}
      goToDashboard={() => {}}
      goToDashboardText='Go to dashboard'
      background={background}
      isButtonLightTheme={isLightButtonStyle}
      imageUrl={icon.url}
      textColour={textColor}
      numberOfActivities={numLessons}
      isPreviewMode={isPreviewMode}
      isInAdminPanel
      getCompletionTicksAriaLabel={getIntlCompletionTicksAriaLabel(intl)}
      tickBoxAltText={intl.formatMessage({id: 'Image.alt.icon.tick'})}
    />
  );
};

const getOutcomesComponent = ({intl, params}) => {
  const {
    title,
    cheer,
    background,
    outcomes,
    fontColour,
    button,
    isPreviewMode = false,
    isLightButtonStyle,
    hidePreviewButtons,
  } = params;
  const textColor = getTextColourOrTextPrimary(fontColour);
  const defaultTitle = getMessage('EditOutcomes.titlePlaceholder', {}, intl);
  const defaultOutcome = (i) =>
    getMessage('EditOutcomes.outcomePlaceholder', {n: i + 1}, intl);
  const defaultButtonText = intl.formatMessage({id: 'Button.next'});

  return (
    <LearningOutcomesSlide
      title={title || defaultTitle}
      cheer={cheer}
      background={background}
      editMode={true}
      textColor={textColor}
      outcomes={outcomes.map((outcome, i) => outcome || defaultOutcome(i))}
      button={!hidePreviewButtons && getButtonParam(button, defaultButtonText)}
      isButtonLightTheme={isLightButtonStyle}
      isPreviewMode={isPreviewMode}
    />
  );
};

const getResultsComponent = ({params, intl}) => {
  const {
    message,
    passMessage,
    failMessage,
    minimumScore,
    isPassing,
    isPreviewMode = false,
    background,
    isLightButtonStyle,
    fontColour,
    numberOfModuleActivities,
  } = params;
  let messageToShow = message;
  let headingKeySuffix = 'heading';
  if (minimumScore && isPassing) {
    messageToShow = passMessage;
    headingKeySuffix = 'passHeading';
  } else if (minimumScore) {
    messageToShow = failMessage;
    headingKeySuffix = 'failHeading';
  }

  const buttonText = intl.formatMessage({id: 'Button.continue'});

  const numberOfActivitiesText = intl.formatMessage(
    {id: 'Courses.lessonCount'},
    {number: numberOfModuleActivities}
  );

  return (
    <ResultsSlide
      isPreview={isPreviewMode}
      scoreText={intl.formatMessage({
        id: 'Authoring.editResults.preview.scoreText',
      })}
      message={messageToShow}
      heading={intl.formatMessage({
        id: `Authoring.editResults.preview.${headingKeySuffix}`,
      })}
      background={background ?? '#0d003a'}
      textColour={fontColour ?? '#FFFFFF'}
      numberOfTicks={numberOfModuleActivities}
      numberOfActivitiesText={numberOfActivitiesText}
      getCompletionTicksAriaLabel={getIntlCompletionTicksAriaLabel(intl)}
      tickBoxAltText={intl.formatMessage({id: 'Image.alt.icon.tick'})}
    >
      <AssessmentPreviewButton
        isButtonLight={isLightButtonStyle === false ? false : true}
        buttonText={buttonText}
      />
    </ResultsSlide>
  );
};

const getQuestionComponent = ({intl, params}) => {
  const {
    prompt: quizPrompt,
    shuffleAnswers,
    questions,
    quizType,
    url,
    urls,
    thumbnailUrl,
    thumbnailUrls,
    progressButton = {},
    questionIndex = 0,
    isAssessment,
    isPreviewMode = false,
    background,
    fontColour,
    phoneHeight = 0,
  } = params;
  const textColor = getTextColourOrWhite(fontColour);
  const question = questions[questionIndex];
  const imageUrl = urls ? urls[questionIndex] : url || '';
  const thumbnailImageUrl = thumbnailUrls
    ? thumbnailUrls[questionIndex]
    : thumbnailUrl || '';
  const defaultButtonTextKey =
    question.type === QUESTION_TYPES.ORDERING && !isAssessment
      ? 'Button.continueLesson'
      : 'Button.submit';
  const defaultButtonText = intl.formatMessage({id: defaultButtonTextKey});
  const questionsContainImages = !!urls?.join('').split('').length;
  const prompt = quizType === 'mixed' ? question.prompt : quizPrompt;

  return (
    <QuestionPreview
      phoneHeight={phoneHeight}
      isPreview={isPreviewMode}
      prompt={prompt}
      shuffleAnswers={shuffleAnswers}
      question={question}
      questionNumber={questionIndex + 1}
      numberOfQuestions={questions.length}
      imageUrl={imageUrl}
      thumbnailImageUrl={thumbnailImageUrl}
      quizType={quizType}
      background={background}
      textColour={textColor}
      isAssessment={isAssessment}
      button={getButtonParam(progressButton, defaultButtonText)}
      quizQuestionsContainImages={questionsContainImages}
    />
  );
};

const getOrderingTaskComponent = ({params, intl}) => {
  const {
    prompt,
    answers,
    isPreviewMode = false,
    progressButton,
    phoneHeight = 0,
    background,
    fontColour,
  } = params;
  const textColor = getTextColourOrWhite(fontColour);

  const defaultButtonText = intl.formatMessage({id: 'Button.continueLesson'});
  const question = {
    prompt,
    type: QUESTION_TYPES.ORDERING,
    options:
      typeof answers[0]?.text === 'string'
        ? answers
        : answers.map((text) => ({text})),
  };
  return (
    <QuestionPreview
      question={question}
      isPreview={isPreviewMode}
      prompt={prompt}
      quizType='text'
      phoneHeight={phoneHeight}
      button={getButtonParam(progressButton, defaultButtonText)}
      background={background}
      textColour={textColor}
    />
  );
};

const getTaskBoxComponent = ({intl, params}) => {
  const {
    taskBoxTitle,
    taskBoxText,
    background,
    fontColour,
    isLightButtonStyle,
    taskBoxButton,
    type,
    isAssessment,
    isPreviewMode = false,
    isUsingHtmlEditor = false,
  } = params;
  const textColor = getTextColourOrWhite(fontColour);
  const defaultText = isAssessment
    ? intl.formatMessage({id: 'Authoring.taskBox.assessmentTextPlaceholder'})
    : intl.formatMessage({id: `Authoring.taskBox.textPlaceholder.${type}`});
  const buttonDefaultText = intl.formatMessage({
    id: isAssessment ? 'Button.next' : `Button.${type}`,
  });

  return (
    <TextSlide
      title={taskBoxTitle}
      description={taskBoxText || defaultText}
      editMode
      textColor={textColor}
      background={background}
      button={getButtonParam(taskBoxButton, buttonDefaultText)}
      isButtonLightTheme={isLightButtonStyle !== false}
      isPreviewMode={isPreviewMode}
      isUsingHtmlEditor={isUsingHtmlEditor}
    />
  );
};

const getVideoComponent = ({params}) => {
  if (params?.url && params?.url !== '') {
    const videoUrl = getVideoUrl(params.url);
    return (
      <div className={cx('preview-component-container')}>
        {videoUrl.includes('youtube.com') ? (
          <Fragment>
            <img className={cx('icon')} src='/resources/img/play.svg' />
            <div className={cx('overlay')} />
            <VideoPreview {...params} />
          </Fragment>
        ) : (
          <Fragment>
            <img className={cx('icon')} src='/resources/img/play.svg' />
            <div className={cx('overlay')}>
              <img
                className={cx('preview-video-image')}
                src={videoUrl.replace(/\.[^.]*$/, '.jpg')}
              />
            </div>
          </Fragment>
        )}
      </div>
    );
  } else {
    return (
      <div className={cx('video-placeholder')}>
        <img src={'/resources/img/authoring/videoPreview.svg'} />
      </div>
    );
  }
};

const getThirdPartyIntroComponent = ({intl, params}) => {
  const {
    title,
    description,
    background,
    introButton,
    isLightButtonStyle = true,
    fontColour,
    isPreviewMode = false,
    isUsingHtmlEditor = false,
  } = params;
  const textColor = fontColour && getHeadingColourFromTextColour(fontColour);
  const defaultText = intl.formatMessage({
    id: 'Authoring.EditThirdParty.descriptionPlaceholder',
  });
  const buttonDefaultText = intl.formatMessage({id: 'Button.next'});

  return (
    <TextSlide
      title={title}
      description={description || defaultText}
      editMode
      textColor={textColor}
      background={background}
      button={getButtonParam(introButton, buttonDefaultText)}
      isButtonLightTheme={isLightButtonStyle}
      isPreviewMode={isPreviewMode}
      isUsingHtmlEditor={isUsingHtmlEditor}
    />
  );
};

const getThirdPartyContentComponent = ({params, intl}) => {
  const {
    url,
    background,
    button,
    isLightButtonStyle,
    isPreviewMode = false,
  } = params;

  const isValidUrl = isValidEmbeddedUrl(url);

  const showPreview = !isPreviewMode && isValidUrl;

  const buttonDefaultText = intl.formatMessage({id: 'Button.next'});

  const provider = getProviderByUrl(url);
  const urlInIframe = provider
    ? provider.getUrlInIframe(url, window.location.origin)
    : null;

  return (
    <div
      className='third-party'
      style={{
        background,
        width: '100%',
        height: '100%',
        padding: '0 0 80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Fragment>
        {showPreview ? (
          <iframe
            src={urlInIframe}
            frameBorder='0'
            width='100%'
            height='100%'
            data-automation='third-party-iframe'
          />
        ) : (
          <img
            width='100%'
            src='/resources/img/authoring/thirdPartySlide.svg'
            style={{maxWidth: '100%', maxHeight: '100%'}}
          />
        )}
      </Fragment>
      {!isPreviewMode && (
        <PreviewNavButton
          button={getButtonParam(button, buttonDefaultText)}
          isLightTheme={isLightButtonStyle}
          background='transparent'
        />
      )}
    </div>
  );
};

const screenTypeToComponentMap = {
  intro: getTextComponent,
  text: getTextComponent,
  imageSlide: getImageSlideComponent,
  imageTop: getTextComponent,
  imageBottom: getTextComponent,
  learningOutcomes: getOutcomesComponent,
  results: getResultsComponent,
  taskBox: getTaskBoxComponent,
  question: getQuestionComponent,
  orderingTask: getOrderingTaskComponent,
  video: getVideoComponent,
  end: getEndComponent,
  thirdPartyIntro: getThirdPartyIntroComponent,
  thirdParty: getThirdPartyContentComponent,
};

export const getPreviewComponentBySlideType = (type, intl, params, overlay) => {
  const componentToPreview = screenTypeToComponentMap[type];

  if (componentToPreview) {
    return componentToPreview({type, intl, params, overlay});
  }
  return null;
};
